import { Injectable } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { defer, merge, Observable } from 'rxjs';
import { filter, map, pluck, switchMap, take } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class RoutingService {
  constructor(private router: Router, private activatedRoute: ActivatedRoute) {}

  get url(): string {
    return this.router.url;
  }

  get lastUrlComponent(): string {
    return this.url.split('/').pop();
  }

  /**
   * @description
   * If you want to get data from the route, in particular, data specific to the unique route
   * you are in, you will use this method.
   *
   * It would look for the farthest away route created, this means that it will look
   * for every children of the route until it no longer finds more children, and after
   * that it will take the data attribute.
   *
   * @param attribute the name of the attribute inside the data object for the route
   */
  lastRouteData<Type>(attribute: string): Observable<Type> {
    const onChangeRoute$: Observable<Type> = this.router.events.pipe(
      filter((event) => event instanceof NavigationEnd),
      map(() => this.activatedRoute.firstChild),
      switchMap((route) => {
        while (route.firstChild) {
          route = route.firstChild;
        }
        return route.data;
      }),
      pluck(attribute),
      filter((value) => value !== null)
    );
    let routeData = this.activatedRoute.firstChild;
    while (routeData.firstChild) {
      routeData = routeData.firstChild;
    }
    return merge(onChangeRoute$, routeData.data.pipe(pluck(attribute)));
  }

  lastRouteParam(): string {
    return this.url
      .split('/')
      .filter((x) => !isNaN(+x))
      .pop();
  }

  redirect(url: string): void {
    this.router.navigate([url]);
  }

  queryParam<Type>(param: string): Observable<Type> {
    return this.activatedRoute.queryParams.pipe(pluck(param));
  }

  param(param: string): Observable<string> {
    const onChangeRoute$: Observable<string> = this.router.events.pipe(
      filter((event) => event instanceof NavigationEnd),
      map(() => this.activatedRoute.firstChild),
      switchMap((route) => {
        while (route.firstChild) {
          route = route.firstChild;
        }
        return route.params;
      }),
      pluck(param),
      filter((value) => value !== null)
    );
    let routeData = this.activatedRoute.firstChild;
    while (routeData.firstChild) {
      routeData = routeData.firstChild;
    }
    return merge(onChangeRoute$, routeData.params.pipe(pluck(param)));
  }

  /**
   * @description
   * Obtain route data, in particular, data specific to the current active route
   * Get the correct data regardless of route changes
   */
  getRouteData<Type>(attribute: string): Observable<Type> {
    const extractData = (route: ActivatedRoute): Observable<Type> => {
      while (route.firstChild) {
        route = route.firstChild;
      }
      return route.data.pipe(
        pluck(attribute),
        filter((val): val is Type => val != null)
      );
    };

    const initial$ = defer(() =>
      extractData(this.activatedRoute).pipe(take(1))
    );

    const onChangeRoute$ = this.router.events.pipe(
      filter((event) => event instanceof NavigationEnd),
      map(() => extractData(this.activatedRoute)),
      // map(() => this.activatedRoute),
      switchMap(() => extractData(this.activatedRoute))
    );

    return merge(initial$, onChangeRoute$);
  }
}
